import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { CornerDownLeft, Search } from "lucide-react";

import type { TrackedSymbol } from "../api/types";
import { fuzzyMatch } from "../lib/fuzzy";
import { Overlay } from "./Overlay";
import { SignalPill } from "./SignalPill";

interface CommandPaletteProps {
  open: boolean;
  symbols: TrackedSymbol[];
  onClose: () => void;
  onSelect: (symbol: string) => void;
  onHover?: (symbol: string) => void;
}

const MAX_RESULTS = 50;

export function CommandPalette({
  open,
  symbols,
  onClose,
  onSelect,
  onHover,
}: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Reset + focus whenever the palette opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    setQuery("");
    setActive(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 20);
    return () => window.clearTimeout(id);
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) {
      return symbols.slice(0, MAX_RESULTS);
    }
    return symbols
      .map((item) => {
        const haystack = item.company_name
          ? `${item.symbol} ${item.company_name}`
          : item.symbol;
        const score = fuzzyMatch(q, haystack);
        return score === null ? null : { item, score };
      })
      .filter((entry): entry is { item: TrackedSymbol; score: number } => entry !== null)
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_RESULTS)
      .map((entry) => entry.item);
  }, [query, symbols]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    const node = listRef.current?.children[active] as HTMLElement | undefined;
    node?.scrollIntoView({ block: "nearest" });
    if (onHover && results[active]) {
      onHover(results[active].symbol);
    }
  }, [active, results, onHover]);

  if (!open) {
    return null;
  }

  const choose = (symbol: string) => {
    onSelect(symbol);
    onClose();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => Math.min(results.length - 1, index + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => Math.max(0, index - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const choice = results[active];
      if (choice) {
        choose(choice.symbol);
      }
    }
  };

  return (
    <Overlay onClose={onClose} align="top">
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Search symbols">
        <div className="cmdk-search">
          <Search className="cmdk-search-icon" aria-hidden />
          <input
            ref={inputRef}
            className="cmdk-input"
            placeholder="Search symbols or companies…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="off"
            spellCheck={false}
            aria-label="Search symbols"
          />
          <kbd className="cmdk-esc">Esc</kbd>
        </div>

        {results.length === 0 ? (
          <p className="cmdk-empty">No matches for “{query.trim()}”.</p>
        ) : (
          <ul className="cmdk-list" ref={listRef}>
            {results.map((item, index) => (
              <li
                key={item.symbol}
                className={`cmdk-item${index === active ? " is-active" : ""}`}
                onMouseEnter={() => setActive(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(item.symbol);
                }}
              >
                <span className="cmdk-symbol">{item.symbol}</span>
                <span className="cmdk-company">{item.company_name || "—"}</span>
                <SignalPill signal={item.signal} size="sm" />
              </li>
            ))}
          </ul>
        )}

        <div className="cmdk-footer">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> navigate
          </span>
          <span>
            <kbd>
              <CornerDownLeft size={11} aria-hidden />
            </kbd>{" "}
            select
          </span>
          <span className="cmdk-count">{results.length} shown</span>
        </div>
      </div>
    </Overlay>
  );
}
